'use client';

import type React from 'react';

import { SaveStatusIcon, type SaveStatus } from '@/components/SaveStatusIcon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowDown,
  ArrowUp,
  Bot,
  Brain,
  CheckCircle2,
  DollarSign,
  Loader2,
  SortAsc,
  SortDesc,
  Text,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { SubscriptionStatus } from '../components/SubscriptionStatus';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { useSubscription } from '../hooks/useSubscription';

interface ModelOption {
  value: string;
  label: string;
  beta?: boolean;
  alpha?: boolean;
  speed: number; // 0-10
  thinking: number; // 0-10
  cost: number; // 0-10
}

type SortField = 'label' | 'thinking' | 'cost' | 'speed';
type SortDirection = 'asc' | 'desc';

const ageRangeOptions = [
  { value: 12, label: 'Plotting Padawan (I got a crayon!)' },
  { value: 25, label: 'Typing Jedi (Words come easily)' },
  { value: 40, label: 'Literary Legend (Netflix, call me)' },
] as const;

const modelOptions: ModelOption[] = [
  { value: '4o-mini', label: 'GPT 4o Mini (recommended)', speed: 6, thinking: 7, cost: 2 },
  { value: 'o3-mini', label: 'GPT O3 Mini (fast reasoning)', speed: 9, thinking: 6, cost: 3 },
  { value: '4o', label: 'GPT 4o', speed: 7, thinking: 9, cost: 8 },
  { value: 'claude', label: 'Claude 3.7 thinking', speed: 3, thinking: 8, cost: 7 },
  { value: 'gemini', label: 'Gemini (Flash Pro)', speed: 10, thinking: 6, cost: 5 },
  { value: 'o1-mini', label: 'GPT O1 Mini (preview)', beta: true, speed: 9, thinking: 8, cost: 6 },
  { value: 'o1', label: 'GPT O1 (preview, reasoning)', beta: true, speed: 5, thinking: 10, cost: 9 },
];

// Add development-only models
const devModelOptions: ModelOption[] =
  process.env.NODE_ENV === 'development'
    ? [
        { value: 'gpt45', label: 'GPT 4.5 ($$$$)', alpha: true, speed: 1, thinking: 9, cost: 10 },
        { value: 'nova', label: 'Nova (Amazon Nova Pro)', alpha: true, speed: 4, thinking: 5, cost: 3 },
        { value: 'nova-lite', label: 'Nova (Amazon Nova Lite)', alpha: true, speed: 8, thinking: 4, cost: 1 },

        // TODO: update
        {
          value: 'gemini25pro',
          label: 'Gemini 2.5 Pro (preview, thinking)',
          beta: true,
          speed: 5,
          thinking: 10,
          cost: 9,
        },
      ]
    : [];

const allModelOptions = [...modelOptions, ...devModelOptions];

const settingsSchema = z.object({
  ageRange: z.number().refine((value) => [12, 25, 40].includes(value), {
    message: 'Age range must be 12, 25, or 40',
  }),
  defaultModel: z.string().refine((value) => allModelOptions.some((option) => option.value === value), {
    message: 'Invalid model selection',
  }),
  useDescriptiveChat: z.boolean(),
});

type SettingsFormValues = z.infer<typeof settingsSchema>;

interface UserSettings {
  ageRange?: number;
  defaultModel?: string;
  useDescriptiveChat?: boolean;
  creditsConsumed?: number;
  creditLimit?: number;
  subscription?: string;
}

// Component to render a property bar
function PropertyBar({
  value,
  maxValue = 10,
  label,
  icon,
  color,
}: {
  value: number;
  maxValue?: number;
  label: string;
  icon: React.ReactNode;
  color: string;
}) {
  const percentage = (value / maxValue) * 100;

  return (
    <div className="flex items-center gap-2 mb-1">
      <div className="flex items-center gap-1 w-24">
        {icon}
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <div className="relative h-2 w-24 bg-gray-200 rounded-full overflow-hidden dark:bg-gray-700">
        <div className={`absolute top-0 left-0 h-full rounded-full ${color}`} style={{ width: `${percentage}%` }} />
      </div>
      <span className="text-xs font-medium ml-1">{value}/10</span>
    </div>
  );
}

export default function Settings() {
  const { toast } = useToast();
  const { apiCall } = useAuthenticatedApi();
  const { subscriptionSpend, setSubscriptionSpend, subscriptionLimit, setSubscriptionLimit } = useSubscription();
  const [saveStatus, setSaveStatus] = useState<Record<keyof SettingsFormValues, SaveStatus>>({
    ageRange: 'idle',
    defaultModel: 'idle',
    useDescriptiveChat: 'idle',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState<string>();
  const [savingModelId, setSavingModelId] = useState<string | null>(null);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('label');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [sortedModels, setSortedModels] = useState<ModelOption[]>(allModelOptions);

  const form = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      ageRange: 25,
      defaultModel: '4o-mini',
      useDescriptiveChat: false,
    },
  });

  // Sort models when sort parameters change
  useEffect(() => {
    const sorted = [...allModelOptions].sort((a, b) => {
      if (sortField === 'label') {
        const comparison = a.label.localeCompare(b.label);
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        const aValue = a[sortField];
        const bValue = b[sortField];
        const comparison = aValue - bValue;
        return sortDirection === 'asc' ? comparison : -comparison;
      }
    });

    setSortedModels(sorted);
  }, [sortField, sortDirection]);

  // Handle sort button click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon based on field and direction
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return null;
    }

    if (field === 'label') {
      return sortDirection === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />;
    } else {
      return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const response = await apiCall<UserSettings>('/user', 'GET');
        if (response === undefined) return;
        if (response) {
          form.reset({
            ageRange: response.ageRange && [12, 25, 40].includes(response.ageRange) ? response.ageRange : 25,
            defaultModel: response.defaultModel || '4o-mini',
            useDescriptiveChat: response.useDescriptiveChat || false,
          });
          setSubscriptionSpend(response.creditsConsumed || 0);
          setSubscriptionLimit(response.creditLimit || 0);
          setCurrentSubscription(response.subscription);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast({
          title: 'Error',
          description: 'Failed to load settings',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [apiCall, form, setSubscriptionLimit, setSubscriptionSpend, toast]);

  const handleSettingChange = async (key: keyof SettingsFormValues, value: unknown) => {
    setSaveStatus((prev) => ({ ...prev, [key]: 'saving' }));
    try {
      await apiCall('/settings', 'PUT', {
        settingName: key,
        settingValue: value,
      });
      setSaveStatus((prev) => ({ ...prev, [key]: 'saved' }));
      toast({
        title: 'Setting updated',
        description: `${key} has been updated successfully.`,
      });
    } catch (error) {
      console.error(`Error saving ${key}:`, error);
      setSaveStatus((prev) => ({ ...prev, [key]: 'idle' }));
      toast({
        title: 'Error',
        description: `Failed to save ${key}`,
        variant: 'destructive',
      });
    }
    setTimeout(() => {
      setSaveStatus((prev) => ({ ...prev, [key]: 'idle' }));
    }, 3000);
  };

  const handleModelChange = async (modelValue: string) => {
    setSavingModelId(modelValue);
    form.setValue('defaultModel', modelValue);

    try {
      await apiCall('/settings', 'PUT', {
        settingName: 'defaultModel',
        settingValue: modelValue,
      });

      toast({
        title: 'Default model updated',
        description: `Your default AI model has been updated successfully.`,
      });
    } catch (error) {
      console.error('Error saving default model:', error);
      toast({
        title: 'Error',
        description: 'Failed to update default model',
        variant: 'destructive',
      });
    } finally {
      setSavingModelId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-10">
        <div className="grid gap-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
              <p className="text-muted-foreground">Manage your preferences and AI settings</p>
            </div>
          </div>
          <div className="container mx-auto py-10 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="grid gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
            <p className="text-muted-foreground">Manage your preferences and AI settings</p>
          </div>
        </div>

        {isLoading ? (
          <div className="container mx-auto py-10 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="grid gap-6">
            <SubscriptionStatus
              subscriptionSpend={subscriptionSpend}
              subscriptionLimit={subscriptionLimit}
              currentlySubscribed={
                currentSubscription !== undefined && !['', 'cancelled', 'ending', 'trial'].includes(currentSubscription)
              }
            />

            <Form {...form}>
              <form className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>AI Preferences</CardTitle>
                    <CardDescription>Customize how the AI interacts with you</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <FormField
                      control={form.control}
                      name="ageRange"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Approximate Age Range</FormLabel>
                          <FormDescription>
                            This affects how the system communicates with you from an "author" point of view
                          </FormDescription>
                          <FormControl>
                            <div className="flex items-center">
                              <RadioGroup
                                onValueChange={(value) => {
                                  const numValue = Number.parseInt(value, 10);
                                  field.onChange(numValue);
                                  handleSettingChange('ageRange', numValue);
                                }}
                                value={field.value.toString()}
                                className="grid grid-cols-3 gap-4"
                              >
                                {ageRangeOptions.map((option) => (
                                  <FormItem key={option.value}>
                                    <FormControl>
                                      <div className="space-x-2">
                                        <RadioGroupItem
                                          value={option.value.toString()}
                                          id={option.value.toString()}
                                          className="peer sr-only"
                                        />
                                        <label
                                          htmlFor={option.value.toString()}
                                          className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary"
                                        >
                                          <span className="text-sm font-medium">{option.label}</span>
                                        </label>
                                      </div>
                                    </FormControl>
                                  </FormItem>
                                ))}
                              </RadioGroup>
                              <SaveStatusIcon status={saveStatus.ageRange} />
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="useDescriptiveChat"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Descriptive Chat</FormLabel>
                            <FormDescription>
                              Enable more descriptive text in character chats, including thoughts and surroundings
                            </FormDescription>
                          </div>
                          <div className="flex items-center">
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={(value) => {
                                  field.onChange(value);
                                  handleSettingChange('useDescriptiveChat', value);
                                }}
                              />
                            </FormControl>
                            <SaveStatusIcon status={saveStatus.useDescriptiveChat} />
                          </div>
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </form>
            </Form>

            <Card>
              <CardHeader>
                <CardTitle>AI Models</CardTitle>
                <CardDescription>Select which AI model to use by default</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Sort controls */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="text-sm text-muted-foreground mr-1 self-center">Sort by:</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSort('label')}
                    className={cn('flex items-center gap-1', sortField === 'label' ? 'bg-primary/10' : '')}
                  >
                    <Text className="h-4 w-4" />
                    <span>Name</span>
                    {getSortIcon('label')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSort('thinking')}
                    className={cn('flex items-center gap-1', sortField === 'thinking' ? 'bg-primary/10' : '')}
                  >
                    <Brain className="h-4 w-4 text-indigo-500" />
                    <span>Reasoning</span>
                    {getSortIcon('thinking')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSort('cost')}
                    className={cn('flex items-center gap-1', sortField === 'cost' ? 'bg-primary/10' : '')}
                  >
                    <DollarSign className="h-4 w-4 text-amber-500" />
                    <span>Cost</span>
                    {getSortIcon('cost')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSort('speed')}
                    className={cn('flex items-center gap-1', sortField === 'speed' ? 'bg-primary/10' : '')}
                  >
                    <Zap className="h-4 w-4 text-green-500" />
                    <span>Speed</span>
                    {getSortIcon('speed')}
                  </Button>
                </div>

                <div className="grid gap-6">
                  {sortedModels.map((model) => {
                    const isDefault = form.getValues('defaultModel') === model.value;
                    const isSaving = savingModelId === model.value;

                    return (
                      <div
                        key={model.value}
                        className={cn(
                          'flex flex-col p-4 rounded-lg border',
                          isDefault ? 'bg-muted border-primary' : 'border-border'
                        )}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start gap-3">
                            <Bot className="mt-0.5 h-5 w-5 text-muted-foreground" />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{model.label}</span>
                                {model.beta && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800"
                                  >
                                    BETA
                                  </Badge>
                                )}
                                {model.alpha && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800"
                                  >
                                    ALPHA
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground mt-1">
                                {model.value.includes('mini')
                                  ? 'Faster responses, more concise outputs'
                                  : 'More detailed responses, better understanding'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center">
                            {isDefault ? (
                              <div className="flex items-center text-primary">
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                <span className="text-sm font-medium">Default</span>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleModelChange(model.value)}
                                disabled={isSaving}
                              >
                                {isSaving ? (
                                  <>
                                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                                    Setting...
                                  </>
                                ) : (
                                  'Set default'
                                )}
                              </Button>
                            )}
                          </div>
                        </div>

                        <div className="mt-2">
                          <PropertyBar
                            value={model.speed}
                            label="Speed"
                            icon={<Zap className="h-3 w-3 text-green-500" />}
                            color="bg-green-500"
                          />
                          <PropertyBar
                            value={model.thinking}
                            label="Reasoning"
                            icon={<Brain className="h-3 w-3 text-indigo-500" />}
                            color="bg-indigo-500"
                          />
                          <PropertyBar
                            value={model.cost}
                            label="Cost"
                            icon={<DollarSign className="h-3 w-3 text-amber-500" />}
                            color="bg-amber-500"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
